/**
 * AgenticPaymentUserBalance - Main Component
 *
 * Entry point for the User Balances feature.
 * Manages the two-view flow:
 *   1. Wallet List - shows HBAR wallets with "View Balances" action
 *   2. Balance Detail - shows token balances for a selected wallet
 *
 * Wallets are fetched via server-side pagination (one page at a time).
 * Search is delegated to the server.
 * State is preserved when navigating between views.
 *
 * @component
 */

import React, { useState, useEffect, useCallback } from "react";
import { getRequest } from "../../utils/httpRequest";

// Page components
import WalletTable from "./WalletTable";
import BalanceDetail from "./BalanceDetail";

// Constants & styles
import { VIEWS, API_ENDPOINTS, WALLETS_PER_PAGE } from "./constants";
import {
  pageHeaderStyles,
  colors,
} from "../AgenticPaymentTransactionHistory/styles";
import "./styles.scss";

// Deep-link support: when the page is opened with ?address=… (e.g. the User show
// page's "View balance →" on an agent/genesis wallet), jump straight to that
// wallet's Balance Detail instead of the generic list. user_id is carried so the
// detail view resolves escrow + the Agent Wallets panel against the SAME user the
// admin was looking at, not whoever happens to own that address locally. name is
// the agent's vanity label (not PII); email is intentionally NOT put in the URL.
const readDeepLinkWallet = () => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const address = params.get("address");
  if (!address) return null;
  return {
    address,
    user_id: params.get("user_id") || null,
    name: params.get("name") || null,
    email: null,
  };
};

const AgenticPaymentUserBalance = ({
  hederaNetwork = "testnet",
  hederaChainId = 296,
}) => {
  // If deep-linked to a specific wallet, open its detail view directly.
  const deepLinkWallet = readDeepLinkWallet();

  // View state
  const [currentView, setCurrentView] = useState(
    deepLinkWallet ? VIEWS.BALANCES : VIEWS.WALLETS,
  );
  const [selectedWallet, setSelectedWallet] = useState(deepLinkWallet);

  // Wallet data, server-side paginated
  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [walletsMeta, setWalletsMeta] = useState({
    total: 0,
    page: 1,
    per_page: WALLETS_PER_PAGE,
    total_pages: 1,
  });

  // Search state (persisted across view toggles)
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Track the last-submitted search to avoid redundant fetches
  const [submittedQuery, setSubmittedQuery] = useState("");

  // Fetch a single page of wallets from the server
  const fetchWalletPage = useCallback(async (page = 1, query = "") => {
    setWalletsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: WALLETS_PER_PAGE.toString(),
      });
      if (query.trim()) {
        params.set("q", query.trim());
      }

      const data = await getRequest(
        `${API_ENDPOINTS.HEDERA_WALLETS}?${params.toString()}`,
      );

      if (data.success) {
        setWallets(data.data);
        setWalletsMeta(
          data.meta || {
            total: data.data.length,
            page: page,
            per_page: WALLETS_PER_PAGE,
            total_pages: 1,
          },
        );
      } else {
        setWallets([]);
        setWalletsMeta({
          total: 0,
          page: 1,
          per_page: WALLETS_PER_PAGE,
          total_pages: 1,
        });
      }
    } catch (error) {
      console.error("Error fetching hedera wallets:", error);
      setWallets([]);
    } finally {
      setWalletsLoading(false);
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchWalletPage(1, "");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle page change
  const handlePageChange = useCallback(
    (newPage) => {
      setCurrentPage(newPage);
      fetchWalletPage(newPage, searchQuery);
    },
    [fetchWalletPage, searchQuery],
  );

  // Handle search submit (button click or Enter key)
  const handleSearchSubmit = useCallback(() => {
    setSubmittedQuery(searchQuery);
    setCurrentPage(1);
    fetchWalletPage(1, searchQuery);
  }, [fetchWalletPage, searchQuery]);

  // Handle clearing search
  const handleSearchClear = useCallback(() => {
    setSearchQuery("");
    setSubmittedQuery("");
    setCurrentPage(1);
    fetchWalletPage(1, "");
  }, [fetchWalletPage]);

  // Handle viewing a wallet's balances
  const handleViewBalances = (wallet) => {
    setSelectedWallet(wallet);
    setCurrentView(VIEWS.BALANCES);
  };

  // Handle going back to wallets, preserves page and search state (no refetch)
  const handleBackToWallets = () => {
    setSelectedWallet(null);
    setCurrentView(VIEWS.WALLETS);
  };

  return (
    <div className="liquidity-pool-user-balances">
      {/* Page Header */}
      <div style={pageHeaderStyles.container}>
        <h1 style={pageHeaderStyles.title}>User Balance</h1>
        <p style={pageHeaderStyles.subtitle}>
          View wallet balance and assets for Hedera wallets
        </p>
      </div>

      {/* Content */}
      <div style={{ padding: "20px 0" }}>
        {currentView === VIEWS.WALLETS ? (
          <WalletTable
            wallets={wallets}
            walletsLoading={walletsLoading}
            totalCount={walletsMeta.total}
            currentPage={currentPage}
            totalPages={walletsMeta.total_pages}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSearchSubmit={handleSearchSubmit}
            onSearchClear={handleSearchClear}
            onPageChange={handlePageChange}
            onViewBalances={handleViewBalances}
          />
        ) : (
          <BalanceDetail
            wallet={selectedWallet}
            hederaChainId={hederaChainId}
            onBack={handleBackToWallets}
            onSelectWallet={handleViewBalances}
          />
        )}
      </div>
    </div>
  );
};

export default AgenticPaymentUserBalance;
